import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, SearchableSelect, useToast, usePincodeLookup,
} from '@kss/ui';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import {
  supplierService,
  type Supplier, type SupplierCreate, type SupplierCategory, type MsmeClassification,
} from '@kss/api';
import { decodeId } from '../../utils/idCodec';
import {
  validateEmail, validateGst, validateMobile, validatePan,
} from '../../utils/formatValidators';

/** Supplier-status options for the searchable Status dropdown. */
const STATUS_OPTIONS = [
  { value: 'active',    label: 'Active',    description: 'Can be raised against' },
  { value: 'inactive',  label: 'Inactive',  description: 'Hidden from POs / RFQs' },
  { value: 'pending',   label: 'Pending',   description: 'Awaiting onboarding' },
  { value: 'blacklist', label: 'Blacklisted', description: 'Excluded from purchase flows' },
];

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[80px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

interface FormState {
  /* basics */
  code: string;
  name: string;
  category: SupplierCategory | '';
  email: string;
  phone: string;

  /* tax */
  gstin: string;
  pan: string;
  msmeClassification: MsmeClassification | '';
  msmeNumber: string;
  tdsSection: string;
  isEInvoiceEligible: boolean;

  /* address */
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;

  /* commerce */
  paymentTerms: string;
  creditDays: string;

  /* bank */
  bankName: string;
  bankAccountNumber: string;
  bankIfsc: string;

  /* governance */
  isBlacklisted: boolean;
  blacklistReason: string;
  notes: string;
  isActive: boolean;
  /** Detailed status (the dropdown). `isActive` is auto-derived. */
  statusCode: string;
}

const empty: FormState = {
  code: '', name: '', category: 'RawMaterial', email: '', phone: '',
  gstin: '', pan: '', msmeClassification: 'NotApplicable', msmeNumber: '', tdsSection: '', isEInvoiceEligible: false,
  address: '', city: '', state: '', postalCode: '', country: 'IN',
  paymentTerms: '', creditDays: '30',
  bankName: '', bankAccountNumber: '', bankIfsc: '',
  isBlacklisted: false, blacklistReason: '', notes: '',
  isActive: true, statusCode: 'active',
};

const STEPS = [
  { key: 'basics',     label: 'Basics' },
  { key: 'tax',        label: 'Tax & MSME' },
  { key: 'address',    label: 'Address' },
  { key: 'commerce',   label: 'Commerce' },
  { key: 'bank',       label: 'Bank' },
  { key: 'governance', label: 'Governance' },
  { key: 'review',     label: 'Review' },
];

export default function NewSupplier() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [form, setForm] = useState<FormState>(empty);
  const [step, setStep] = useState(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);
  const [visitedIndices, setVisitedIndices] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k as string]) setErrors((e) => ({ ...e, [k]: '' }));
  }

  // Lightweight inline-error map keyed by field name. Submit-time
  // validation still runs via the `validate()` function below.
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Auto-fill city / state / country from the entered PIN.
  const pinLookup = usePincodeLookup(form.postalCode);
  useEffect(() => {
    if (pinLookup.status !== 'ok') return;
    setForm((f) => ({
      ...f,
      city:    f.city    || pinLookup.city    || f.city,
      state:   f.state   || pinLookup.state   || f.state,
      country: f.country && f.country !== 'IN' ? f.country : (pinLookup.country ?? f.country),
    }));
  }, [pinLookup.status, pinLookup.city, pinLookup.state, pinLookup.country]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try {
          const r = await supplierService.get(id);
          if (cancelled) return;
          setForm({
            ...empty,
            code: r.code ?? '',
            name: r.name ?? '',
            category: (r.category ?? 'RawMaterial') as SupplierCategory,
            email: r.email ?? '',
            phone: r.phone ?? '',
            gstin: r.gstin ?? '',
            pan: r.pan ?? '',
            msmeClassification: (r.msmeClassification ?? 'NotApplicable') as MsmeClassification,
            msmeNumber: r.msmeNumber ?? '',
            tdsSection: r.tdsSection ?? '',
            isEInvoiceEligible: !!r.isEInvoiceEligible,
            address: r.address ?? '',
            city: r.city ?? '',
            state: r.state ?? '',
            postalCode: r.postalCode ?? '',
            country: r.country ?? 'IN',
            paymentTerms: r.paymentTerms ?? '',
            creditDays: r.creditDays != null ? String(r.creditDays) : '30',
            bankName: r.bankName ?? '',
            bankAccountNumber: r.bankAccountNumber ?? '',
            bankIfsc: r.bankIfsc ?? '',
            isBlacklisted: !!r.isBlacklisted,
            blacklistReason: r.blacklistReason ?? '',
            notes: r.notes ?? '',
            isActive: !!r.isActive,
            statusCode: r.isActive === false ? 'inactive' : (r as { status?: string }).status ?? 'active',
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  function validate(): string | null {
    if (!form.name.trim()) return 'Name is required';
    if (form.email) { const e = validateEmail(form.email); if (e) return e; }
    if (form.phone) { const e = validateMobile(form.phone); if (e) return e; }
    if (form.gstin) { const e = validateGst(form.gstin); if (e) return e; }
    if (form.pan)   { const e = validatePan(form.pan);   if (e) return e; }
    if (form.bankIfsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.bankIfsc)) return 'IFSC must be 11 chars (e.g. SBIN0001234)';
    if (form.isBlacklisted && !form.blacklistReason.trim()) return 'Blacklist reason is required when blacklisted';
    return null;
  }

  async function save() {
    const e = validate();
    if (e) { toast.error('Fix errors', e); return; }
    setSaving(true);
    try {
      const payload: SupplierCreate = {
        code: form.code || undefined,
        name: form.name,
        category: (form.category || undefined) as SupplierCategory | undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        gstin: form.gstin || undefined,
        pan: form.pan || undefined,
        msmeClassification: (form.msmeClassification || undefined) as MsmeClassification | undefined,
        msmeNumber: form.msmeNumber || undefined,
        tdsSection: form.tdsSection || undefined,
        isEInvoiceEligible: form.isEInvoiceEligible,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        paymentTerms: form.paymentTerms || undefined,
        creditDays: form.creditDays ? Number(form.creditDays) : undefined,
        bankName: form.bankName || undefined,
        bankAccountNumber: form.bankAccountNumber || undefined,
        bankIfsc: form.bankIfsc || undefined,
        isBlacklisted: form.isBlacklisted,
        blacklistReason: form.blacklistReason || undefined,
        notes: form.notes || undefined,
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await supplierService.update([{ supplierId: id, ...payload } as Supplier]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        await supplierService.create([payload]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/suppliers');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally { setSaving(false); }
  }

  const isLast = step === STEPS.length - 1;
  function goNext() {
    if (step === 0 && !form.name.trim()) { toast.error('Fix errors', 'Name is required'); return; }
    setSavedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(step + 1);
  }
  function goBack() {
    setVisitedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(Math.max(0, step - 1));
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/suppliers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit supplier' : 'New supplier'}
          </h2>
        </div>
        <div className="mb-3">
          <ArrowTabStepper
            compact
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            activeIndex={step}
            savedIndices={savedIndices}
            visitedIndices={visitedIndices}
            onChange={(i) => setStep(i)}
          />
        </div>

        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <>
            {step === 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Code">
                  <input className={inputCls} value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="VEN-0001 (auto if empty)" />
                </Field>
                <Field label="Name" required>
                  <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Industries Pvt Ltd" />
                </Field>
                <Field label="Category">
                  <select className={selectCls} value={form.category} onChange={(e) => set('category', e.target.value as FormState['category'])}>
                    <option value="RawMaterial">Raw material</option>
                    <option value="FinishedGoods">Finished goods</option>
                    <option value="Services">Services</option>
                    <option value="Capex">Capex</option>
                    <option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="Status">
                  <SearchableSelect
                    value={form.statusCode}
                    onChange={(v) => {
                      set('statusCode', v);
                      set('isActive', v === 'active');
                      if (v === 'blacklist') set('isBlacklisted', true);
                    }}
                    options={STATUS_OPTIONS}
                    placeholder="Select status…"
                    searchPlaceholder="Filter…"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => {
                      if (form.phone) {
                        const err = validateMobile(form.phone);
                        if (err) setErrors((s) => ({ ...s, phone: err }));
                      }
                    }}
                    maxLength={10}
                  />
                  {errors.phone && <span className="text-[11px] text-danger">{errors.phone}</span>}
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    onBlur={() => {
                      if (form.email) {
                        const err = validateEmail(form.email);
                        if (err) setErrors((s) => ({ ...s, email: err }));
                      }
                    }}
                  />
                  {errors.email && <span className="text-[11px] text-danger">{errors.email}</span>}
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="GSTIN">
                  <input
                    className={inputCls}
                    value={form.gstin}
                    onChange={(e) => set('gstin', e.target.value.toUpperCase())}
                    onBlur={() => {
                      if (form.gstin) {
                        const err = validateGst(form.gstin);
                        if (err) setErrors((s) => ({ ...s, gstin: err }));
                      }
                    }}
                    maxLength={15}
                    placeholder="22AAAAA0000A1Z5"
                  />
                  {errors.gstin && <span className="text-[11px] text-danger">{errors.gstin}</span>}
                </Field>
                <Field label="PAN">
                  <input
                    className={inputCls}
                    value={form.pan}
                    onChange={(e) => set('pan', e.target.value.toUpperCase())}
                    onBlur={() => {
                      if (form.pan) {
                        const err = validatePan(form.pan);
                        if (err) setErrors((s) => ({ ...s, pan: err }));
                      }
                    }}
                    maxLength={10}
                    placeholder="ABCDE1234F"
                  />
                  {errors.pan && <span className="text-[11px] text-danger">{errors.pan}</span>}
                </Field>
                <Field label="MSME classification">
                  <select className={selectCls} value={form.msmeClassification} onChange={(e) => set('msmeClassification', e.target.value as FormState['msmeClassification'])}>
                    <option value="NotApplicable">Not applicable</option>
                    <option value="Micro">Micro</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                  </select>
                </Field>
                <Field label="MSME / Udyam number">
                  <input className={inputCls} value={form.msmeNumber} onChange={(e) => set('msmeNumber', e.target.value)} placeholder="UDYAM-XX-00-0000000" />
                </Field>
                <Field label="TDS section">
                  <input className={inputCls} value={form.tdsSection} onChange={(e) => set('tdsSection', e.target.value)} placeholder="194C, 194J, 194Q…" />
                </Field>
                <ToggleRow label="E-Invoice eligible" hint="Vendor turnover ≥ ₹5 cr — required for IRN flow" value={form.isEInvoiceEligible} onChange={(v) => set('isEInvoiceEligible', v)} />
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Address</span>
                  <textarea className={textareaCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
                </label>
                {/* PIN first so the auto-fill flows naturally into the other fields below. */}
                <Field label="Postal code">
                  <input
                    className={inputCls}
                    value={form.postalCode}
                    onChange={(e) => set('postalCode', e.target.value.replace(/\D/g, ''))}
                    maxLength={6}
                    placeholder="e.g. 302020"
                  />
                  <span className="text-[11px] text-content-muted">
                    {pinLookup.status === 'loading' && 'Looking up…'}
                    {pinLookup.status === 'ok' && '✓ Auto-fill ready'}
                    {pinLookup.status === 'error' && '⚠ PIN not found'}
                    {pinLookup.status === 'invalid' && '⚠ 6 digits required'}
                  </span>
                </Field>
                <Field label="City">
                  <input
                    className={inputCls}
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    placeholder={pinLookup.city ?? 'e.g. Jaipur'}
                  />
                </Field>
                <Field label="State">
                  <input
                    className={inputCls}
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    placeholder={pinLookup.state ?? 'e.g. Rajasthan'}
                  />
                </Field>
                <Field label="Country">
                  <input
                    className={inputCls}
                    value={form.country}
                    onChange={(e) => set('country', e.target.value)}
                    placeholder={pinLookup.country ?? 'India'}
                  />
                </Field>
                <p className="text-[11px] text-content-muted md:col-span-2">
                  Tip: enter a 6-digit Indian PIN and we&rsquo;ll fill in city, state &amp; country.
                </p>
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Payment terms">
                  <input className={inputCls} value={form.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} placeholder="Net 30, 50% advance, …" />
                </Field>
                <Field label="Credit days">
                  <input type="number" className={inputCls} value={form.creditDays} onChange={(e) => set('creditDays', e.target.value)} />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Bank name">
                  <input className={inputCls} value={form.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="HDFC Bank" />
                </Field>
                <Field label="Account number">
                  <input className={inputCls} value={form.bankAccountNumber} onChange={(e) => set('bankAccountNumber', e.target.value.replace(/\D/g, ''))} maxLength={20} />
                </Field>
                <Field label="IFSC">
                  <input className={inputCls} value={form.bankIfsc} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())} maxLength={11} placeholder="HDFC0001234" />
                </Field>
                <p className="text-[11px] text-content-muted md:col-span-2">
                  Multiple bank accounts live in the SupplierBankAccount module (purchase namespace).
                </p>
              </div>
            )}

            {step === 5 && (
              <div className="grid gap-3 md:grid-cols-2">
                <ToggleRow label="Blacklisted" hint="Blocks new POs to this vendor" value={form.isBlacklisted} onChange={(v) => set('isBlacklisted', v)} />
                {form.isBlacklisted && (
                  <label className="flex flex-col gap-1 md:col-span-2">
                    <span className="text-xs font-medium text-content-secondary">Blacklist reason <span className="text-danger">*</span></span>
                    <textarea className={textareaCls} value={form.blacklistReason} onChange={(e) => set('blacklistReason', e.target.value)} />
                  </label>
                )}
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Internal notes</span>
                  <textarea className={textareaCls} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                </label>
              </div>
            )}

            {step === 6 && (
              <div className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-2">
                <Row k="Code" v={form.code || '—'} />
                <Row k="Name" v={form.name} />
                <Row k="Category" v={form.category || '—'} />
                <Row k="Phone / Email" v={[form.phone, form.email].filter(Boolean).join(' / ') || '—'} />
                <Row k="GSTIN / PAN" v={[form.gstin, form.pan].filter(Boolean).join(' / ') || '—'} />
                <Row k="MSME" v={form.msmeClassification && form.msmeClassification !== 'NotApplicable' ? `${form.msmeClassification} ${form.msmeNumber}` : '—'} />
                <Row k="TDS section" v={form.tdsSection || '—'} />
                <Row k="E-invoice" v={form.isEInvoiceEligible ? 'Eligible' : '—'} />
                <Row k="Address" v={[form.address, form.city, form.state, form.postalCode].filter(Boolean).join(', ') || '—'} />
                <Row k="Payment" v={[form.paymentTerms, form.creditDays && `${form.creditDays} days`].filter(Boolean).join(' · ') || '—'} />
                <Row k="Bank" v={[form.bankName, form.bankAccountNumber, form.bankIfsc].filter(Boolean).join(' · ') || '—'} />
                <Row k="Status" v={form.isBlacklisted ? '🚫 Blacklisted' : (form.isActive ? 'Active' : 'Inactive')} />
              </div>
            )}
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="text-xs text-content-muted">Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/panel/suppliers')} disabled={saving}>Cancel</Button>
            {step > 0 && <Button variant="outline" onClick={goBack} disabled={saving}><ArrowLeft className="h-4 w-4" /> Back</Button>}
            {!isLast && <Button onClick={goNext} disabled={saving}>Next <ArrowRight className="h-4 w-4" /></Button>}
            {isLast && (
              <Button onClick={save} disabled={saving || loading}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save supplier'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">
        {label}{required && <span className="text-danger"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-content-muted">{k}</span>
      <span className="text-sm font-medium text-content-primary">{v || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}

function ToggleRow({
  label, hint, value, onChange,
}: { label: string; hint: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-surface-card-hover">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-primary" />
      <div className="flex-1">
        <div className="text-sm font-medium text-content-primary">{label}</div>
        <div className="text-xs text-content-muted">{hint}</div>
      </div>
    </label>
  );
}
